import { useEffect, type ReactNode } from 'react';
import { useScrollLock } from './use-scroll-lock';

/**
 * A lightweight, accessible modal dialog: a dimmed backdrop with a centered
 * surface, closing on backdrop click or Escape. Styled with design tokens so it
 * flips with the theme. Body scroll is locked while open.
 */
const MAX_WIDTH = {
  lg: 'max-w-lg',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
} as const;

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 'lg',
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Constrains the dialog width; defaults to `lg`. */
  maxWidth?: keyof typeof MAX_WIDTH;
}) {
  // Ref-counted so stacked/nested modals unlock the body exactly once (see
  // useScrollLock). Depends only on `open`, so a parent re-render (e.g. a polling
  // list handing down a fresh onClose) can't re-run it and lose the saved overflow.
  useScrollLock(open);

  // Escape-to-close is separate — it legitimately re-binds when onClose changes,
  // and must never touch the scroll lock.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'color-mix(in srgb, var(--obsidian) 70%, transparent)' }}
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className={`flex max-h-[85vh] w-full ${MAX_WIDTH[maxWidth]} flex-col overflow-hidden rounded-lg border border-hairline bg-surface shadow-pop`}
      >
        <div className="flex items-start justify-between gap-3 border-b border-hairline px-5 py-3">
          <div className="min-w-0 flex-1">{title}</div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="grid h-7 w-7 flex-none place-items-center rounded-sm border border-border text-muted transition-colors hover:border-border-strong hover:bg-surface-2 hover:text-bone"
          >
            ✕
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="border-t border-hairline px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}
